import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import { fetchData } from '../utils/api';
import { extractSkills } from '../utils/helpers';

const CandidateDetail = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCandidate = async () => {
      try {
        setLoading(true);
        const candidatesData = await fetchData('candidates');
        const foundCandidate = candidatesData.find(c => c.id === candidateId);
        
        if (foundCandidate) {
          setCandidate(foundCandidate);
        } else {
          setError('Candidate not found');
        }
      } catch (err) {
        console.error('Error loading candidate:', err);
        setError('Failed to load candidate details');
      } finally {
        setLoading(false);
      }
    };

    loadCandidate();
  }, [candidateId]);

  // Helper function to format dates safely
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    return dateValue;
  };

  const handleBack = () => {
    navigate('/?tab=candidates');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header activeTab="candidates" setActiveTab={() => {}} />
        <main className="max-w-7xl mx-auto px-6 py-6">
          <div className="dashboard-card p-8 text-center max-w-lg mx-auto fade-in">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
            <p className="text-xl font-medium text-gray-700">Loading candidate details...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the candidate information</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header activeTab="candidates" setActiveTab={() => {}} />
        <main className="max-w-7xl mx-auto px-6 py-6">
          <div className="dashboard-card p-8 text-center max-w-lg mx-auto fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-6">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Candidate Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleBack}
              className="btn btn-primary px-6 py-3 rounded-lg font-medium inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Candidates
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header activeTab="candidates" setActiveTab={() => {}} />
      
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="fade-in">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Candidates
          </button>

          {/* Candidate Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 rounded-xl p-6 mb-6 shadow-lg">
            <div className="flex items-start">
              <div className="bg-white/20 rounded-full p-4 mr-6 backdrop-blur-sm">
                <span className="text-3xl font-bold text-white">
                  {candidate.name ? candidate.name.split(' ').map(n => n[0]).join('') : 'N/A'}
                </span>
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">{candidate.name}</h1>
                <p className="text-indigo-100 text-lg mb-4">{candidate.email || 'No email provided'}</p>
                
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                    candidate.stage === 'Hired' ? 'bg-green-100 text-green-800' :
                    candidate.stage === 'Rejected' ? 'bg-red-100 text-red-800' :
                    candidate.stage === 'Interview' || candidate.stage === 'Final Interview' ? 'bg-blue-100 text-blue-800' :
                    candidate.stage === 'Phone Screen' || candidate.stage === 'Technical Assessment' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-white/20 text-white'
                  }`}>
                    {candidate.stage}
                  </span>
                  
                  {candidate.role && (
                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium">
                      {candidate.role}
                    </span>
                  )}
                  
                  {candidate.pipelineDate && (
                    <span className="text-indigo-100 text-sm">
                      Applied: {formatDate(candidate.pipelineDate)}
                    </span>
                  )}
                </div>

                {candidate.linkedIn && (
                  <div className="mt-4">
                    <a 
                      href={candidate.linkedIn} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-white/80 hover:text-white transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      LinkedIn Profile
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Role Details */}
          <div className="dashboard-card p-6">
            <div className="flex items-center mb-6">
              <svg className="w-6 h-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Role Details</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Role</div>
                <div className="text-gray-900 font-medium">{candidate.role || 'N/A'}</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Department</div>
                <div className="text-gray-900">{candidate.department || 'N/A'}</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Client</div>
                <div className="text-gray-900">{candidate.client || 'N/A'}</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Seniority</div>
                <div className="text-gray-900">{candidate.seniority || 'N/A'}</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Salary Offer</div>
                <div className="text-gray-900 font-medium">
                  {candidate.salary && candidate.salary.amount ? 
                    `${candidate.salary.currency || '€'}${candidate.salary.amount.toLocaleString()}` : 
                    'N/A'}
                </div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Source</div>
                <div className="text-gray-900">{candidate.source || 'N/A'}</div>
              </div>
              {candidate.sourceDetail && (
                <div className="flex justify-between py-3">
                  <div className="text-gray-600 font-medium">Source Detail</div>
                  <div className="text-gray-900">{candidate.sourceDetail}</div>
                </div>
              )}
            </div>
          </div>

          {/* Skills & Experience */}
          <div className="dashboard-card p-6">
            <div className="flex items-center mb-6">
              <svg className="w-6 h-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Skills & Experience</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <div className="text-gray-600 font-medium mb-3">Skills</div>
                <div className="flex flex-wrap gap-2">
                  {extractSkills(candidate.skills).map((skill, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <div className="text-gray-600 font-medium mb-3">Experience</div>
                <div className="text-gray-700 leading-relaxed">
                  {candidate.experience || 'No experience details available.'}
                </div>
              </div>
            </div>
          </div>

          {/* Application Timeline */}
          <div className="dashboard-card p-6">
            <div className="flex items-center mb-6">
              <svg className="w-6 h-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800">Application Timeline</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Applied</div>
                <div className="text-gray-900">{formatDate(candidate.pipelineDate)}</div>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <div className="text-gray-600 font-medium">Current Stage</div>
                <div className="text-gray-900 font-medium">{candidate.stage}</div>
              </div>
              {candidate.offerDate && (
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <div className="text-gray-600 font-medium">Offer Date</div>
                  <div className="text-gray-900">{formatDate(candidate.offerDate)}</div>
                </div>
              )}
              {candidate.signDate && (
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <div className="text-gray-600 font-medium">Offer Signed</div>
                  <div className="text-gray-900">{formatDate(candidate.signDate)}</div>
                </div>
              )}
              {candidate.startDate && (
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <div className="text-gray-600 font-medium">Start Date</div>
                  <div className="text-gray-900">{formatDate(candidate.startDate)}</div>
                </div>
              )}
              {candidate.timeToHire && (
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <div className="text-gray-600 font-medium">Time to Hire</div>
                  <div className="text-gray-900 font-medium">{candidate.timeToHire} days</div>
                </div>
              )}
              {candidate.timeToFill && (
                <div className="flex justify-between py-3">
                  <div className="text-gray-600 font-medium">Time to Fill</div>
                  <div className="text-gray-900 font-medium">{candidate.timeToFill} days</div>
                </div>
              )}
            </div>
          </div>

          {/* Comments Section */}
          {candidate.comments && (
            <div className="dashboard-card p-6">
              <div className="flex items-center mb-6">
                <svg className="w-6 h-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-800">Comments</h2>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">{candidate.comments}</div>
              </div>
            </div>
          )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-4">
            <button 
              onClick={handleBack}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Back to Candidates
            </button>
            <button 
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Export Profile
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CandidateDetail;